import React from 'react'
import { Form, Input, Select, Switch, DatePicker, Button, Space, Divider, Typography } from 'antd'
import dayjs from 'dayjs'
import { RELEASE_ITEM_KIND_OPTIONS } from './types'

const { Text } = Typography

/**
 * The "What's New" authoring form (shared by Create + Edit — mirrors
 * notification_rules/RuleFormFields.tsx). `published_at` is the entire publish
 * mechanism (see backend/supabase/migrations/20260916120000_releases.sql):
 * NULL is a draft nobody but an admin can see; setting it publishes the release
 * and fixes its place in the newest-first order members see it in. The
 * "Status" Switch below is a plain UI control (not itself a form field) that
 * drives the real `published_at` value — turning it on stamps "now", turning
 * it off nulls it back out to a draft. The DatePicker underneath stays
 * REGISTERED at all times (via AntD's `hidden` prop, not by unmounting the
 * Form.Item) so unpublishing always submits an explicit `published_at: null`
 * instead of silently omitting the field from the save payload — an unmounted
 * Form.Item's value is never included in what rc-field-form submits, so a real
 * removal (not just hiding it) would leave a previously-published release
 * stuck published on save. See RuleFormFields.tsx for the same lesson learned
 * the hard way with notification_template's hidden recipient/route keys.
 */
export function ReleaseFormFields() {
  const form = Form.useFormInstance()
  const publishedAt = Form.useWatch('published_at', form) as string | undefined | null
  const isPublished = Boolean(publishedAt)

  return (
    <>
      {/* ── Header ─────────────────────────────────────────── */}
      <Divider orientation="left">Header</Divider>
      <Form.Item
        label="Title"
        name="title"
        rules={[{ required: true, message: 'Title is required' }]}
        extra="Outcome-led, not feature-led — what can the member now do or see? e.g. “Find classmates on the map”."
      >
        <Input placeholder="Find classmates on the map" />
      </Form.Item>
      <Form.Item
        label="Summary"
        name="summary"
        extra="One line. This is the only text shown on the release's card in the community feed."
      >
        <Input.TextArea
          rows={2}
          placeholder="See where your classmates live on a map of the alumni network."
        />
      </Form.Item>

      {/* ── What changed ───────────────────────────────────── */}
      <Divider orientation="left">What changed</Divider>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12.5 }}>
        Two or three sentences per item, at most. Start with what the member can now do —
        &ldquo;You can now&hellip;&rdquo; is a good forcing function. Phrase fixes as outcomes
        (&ldquo;Your class year now saves the first time&rdquo;), never &ldquo;bug fixes and
        performance improvements.&rdquo;
      </Text>
      <Form.List name="items">
        {(fields, { add, remove, move }) => (
          <>
            {fields.map(({ key, name, ...rest }, index) => (
              <div
                key={key}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}
              >
                <Form.Item
                  {...rest}
                  name={[name, 'kind']}
                  initialValue="new"
                  rules={[{ required: true, message: 'Kind is required' }]}
                  style={{ marginBottom: 0, width: 130, flexShrink: 0 }}
                >
                  <Select options={RELEASE_ITEM_KIND_OPTIONS} />
                </Form.Item>
                <Form.Item
                  {...rest}
                  name={[name, 'text']}
                  rules={[{ required: true, message: 'Item text is required' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="You can now find classmates near you on a map of the alumni network."
                  />
                </Form.Item>
                <Space direction="vertical" size={4} style={{ flexShrink: 0 }}>
                  <Space size={4}>
                    <Button
                      size="small"
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      aria-label="Move item up"
                    >
                      ↑
                    </Button>
                    <Button
                      size="small"
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      aria-label="Move item down"
                    >
                      ↓
                    </Button>
                  </Space>
                  <Button danger size="small" block onClick={() => remove(name)}>
                    Remove
                  </Button>
                </Space>
              </div>
            ))}
            <Button onClick={() => add({ kind: 'new', text: '' })} block>
              + Add item
            </Button>
          </>
        )}
      </Form.List>

      {/* ── Publishing ─────────────────────────────────────── */}
      <Divider orientation="left">Publishing</Divider>
      <Form.Item label="Status">
        <Switch
          checked={isPublished}
          checkedChildren="Published"
          unCheckedChildren="Draft"
          onChange={(checked) => {
            form.setFieldValue('published_at', checked ? dayjs().toISOString() : null)
          }}
        />
        <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            A draft is invisible to members — only admins can see or edit it here. Turning this on
            publishes the release immediately using the date below, which also fixes its place in
            the newest-first order members see releases in.
          </Text>
        </div>
      </Form.Item>
      <Form.Item
        label="Published date"
        name="published_at"
        hidden={!isPublished}
        getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
        normalize={(value) => (value && dayjs.isDayjs(value) ? value.toISOString() : value)}
        extra="Backdate or schedule a specific moment, or leave it as the time you switched this on."
      >
        <DatePicker showTime style={{ width: 280 }} />
      </Form.Item>
      <Form.Item
        label="Show in feed"
        name="show_in_feed"
        valuePropName="checked"
        extra="When on, this release also gets a card in the community feed in addition to the dedicated What's New screen."
      >
        <Switch />
      </Form.Item>
    </>
  )
}
